import type { ReactElement } from "react";
import { CardLogo, TextButton } from "@industriousoffice/react-components";
import { useTranslation } from "react-i18next";
import {
  ZuoraPaymentMethod,
  ZuoraPaymentMethodTypes,
} from "@/api/opb/opbTypes";

import { isExpiredCard } from "../utils/payment-method-settings";

export function paymentMethodLabel(
  method: ZuoraPaymentMethod,
  includeDetails = true
): string {
  let number: string;
  switch (method.type) {
    case ZuoraPaymentMethodTypes.CreditCard:
      number = method.cardNumber;
      break;
    case ZuoraPaymentMethodTypes.ACH:
      number = method.bankAccountNumber;
      break;
    case ZuoraPaymentMethodTypes.Sepa:
      number = method.IBAN || method.accountNumber;
      break;
    default:
      number = method.accountNumber;
  }
  const methodType =
    method.type === ZuoraPaymentMethodTypes.ACH ? "ACH" : method.type;
  const identifier = `${
    method.type === ZuoraPaymentMethodTypes.CreditCard
      ? method.cardType
      : methodType
  } •••• ${number?.slice(-4) || "••••"}`;
  if (!includeDetails) return identifier;
  return [
    method.nickname?.trim(),
    identifier,
    method.type === ZuoraPaymentMethodTypes.CreditCard
      ? `${String(method.expMonth).padStart(2, "0")}/${method.expYear}`
      : undefined,
  ]
    .filter(Boolean)
    .join(" · ");
}

interface PaymentMethodCardProps {
  method: ZuoraPaymentMethod;
  isDefault: boolean;
  isBusy: boolean;
  onEdit: () => void;
  onMakeDefault: () => void;
}

export function PaymentMethodCard({
  method,
  isDefault,
  isBusy,
  onEdit,
  onMakeDefault,
}: PaymentMethodCardProps): ReactElement {
  const { t } = useTranslation("paymentMethods");
  const card =
    method.type === ZuoraPaymentMethodTypes.CreditCard ? method : undefined;
  const isExpired = isExpiredCard(method);
  const label = paymentMethodLabel(method);
  return (
    <article className="saved-payment-method" aria-label={label}>
      <div className="saved-method-icon">
        {card != null ? (
          <CardLogo cardType={card.cardType} />
        ) : (
          <span aria-hidden="true">{method.type}</span>
        )}
      </div>
      <div className="saved-method-details">
        {method.nickname && <strong>{method.nickname}</strong>}
        <div className="saved-method-label">
          <span className="capitalize">
            {paymentMethodLabel(method, false)}
          </span>
          {isDefault && <span className="payment-status">{t("Default")}</span>}
        </div>
        {card != null && (
          <p className={isExpired ? "payment-error" : ""}>
            {t(isExpired ? "Expired" : "Expires")}{" "}
            {String(card.expMonth).padStart(2, "0")}/{card.expYear}
          </p>
        )}
        {method.type === ZuoraPaymentMethodTypes.ACH && (
          <p>
            {method.bankName} ·{" "}
            <span className="capitalize">{method.bankAccountType}</span>
          </p>
        )}
        {method.status !== "active" && (
          <p>
            {t("Status")}: {method.status}
          </p>
        )}
      </div>
      <div className="saved-method-actions">
        <TextButton
          type="button"
          disabled={isBusy}
          aria-label={`${t("Edit")} ${label}`}
          onClick={onEdit}
        >
          {t("Edit")}
        </TextButton>
        {!isDefault && (
          <TextButton
            type="button"
            disabled={isBusy || isExpired}
            aria-label={`${t("Make default")}: ${label}`}
            onClick={onMakeDefault}
          >
            {t("Make default")}
          </TextButton>
        )}
      </div>
    </article>
  );
}
